/**
 * EnablesPips — compact on/off indicator row for the roster table's Enables
 * column (WSRPDM-0012): global · tenant · one pip per service. Read-only
 * summary; the detail panel owns the actual switches.
 */
import { Box, Stack, Tooltip } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { jobTypeLabel } from '@/domain/registry';
import type { ServiceEnableView } from '@/api/wsr/types';

interface EnablesPipsProps {
  global: boolean;
  tenant: boolean;
  services: ServiceEnableView[];
}

function Pip({
  label,
  on,
  stateLabelOverride,
}: {
  label: string;
  on: boolean;
  /** WSRPDM-0047 — overrides the plain On/Off wording, used to disclose an
   *  enabled-but-not-running service rather than reading like a plain "Off". */
  stateLabelOverride?: string | undefined;
}) {
  const { t } = useCopy();
  const stateLabel =
    stateLabelOverride ?? (on ? t('tenants.enables.on', 'On') : t('tenants.enables.off', 'Off'));
  return (
    <Tooltip title={`${label}: ${stateLabel}`}>
      <Box
        component="span"
        role="img"
        aria-label={`${label}: ${stateLabel}`}
        data-on={on}
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          display: 'inline-block',
          bgcolor: on ? 'success.main' : 'action.disabled',
        }}
      />
    </Tooltip>
  );
}

export function EnablesPips({ global, tenant, services }: EnablesPipsProps) {
  const { t } = useCopy();
  return (
    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
      <Pip label={t('tenants.enables.global', 'Global')} on={global} />
      <Pip label={t('tenants.enables.tenant', 'Tenant')} on={tenant} />
      {services.map((service) => {
        // WSRAPI-0079 — render the outcome, not the control. `?? service.enabled` is a
        // FIELD fallback (a different server-published value), not a rule re-derivation:
        // it degrades to today's meaning only until `effective` is always present.
        const on = service.effective ?? service.enabled;
        const mismatch = service.effective !== undefined && service.effective !== service.enabled;
        return (
          <Pip
            key={service.jobType}
            label={jobTypeLabel(service.jobType, t)}
            on={on}
            stateLabelOverride={
              mismatch ? t('tenants.enables.onNotRunning', 'On, but not running') : undefined
            }
          />
        );
      })}
    </Stack>
  );
}
